import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Box } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Route as RouteIcon, FilterList as FilterIcon } from '@mui/icons-material';
import { MapProvider, useMapContext } from '../../context/MapContext';
import ChargerInfoPanel from './ChargerInfoPanel';
// Force reload to fix icon imports

// Components
import MapContainer from './MapContainer';
import SearchBar from './SearchBar';
import MapControls from './MapControls';
import FilterPanel from './FilterPanel';
import ChargerHoverCard from './ChargerHoverCard';
import RoutePlannerPanel from './RoutePlannerPanel';
import RouteDetailsPanel from './ResponsiveRouteDetailsPanel';

const MapContent = ({ chargers = [], searchBarEnabled = true, isMapPage = false, dashboardMode = false, height = '100%' }) => {
  const {
    map,
    setChargers,
    filteredChargers,
    setFilteredChargers,
    selectedCharger,
    hoveredCharger,
    chargerGroup,
    showChargerInfo,
    showFilterPanel,
    setShowFilterPanel,
    showRoutePlanner,
    setShowRoutePlanner,
    activeFilters,
    setActiveFilters,
    handleChargerSelect,
    handleChargerHover,
    handleCloseChargerInfo,
    setMapCenter,
    setMapZoom,
    routePlannerMode,
    setRoutePlannerMode,
  } = useMapContext();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const containerStyles = useMemo(() => {
    const borderColor = isDarkMode
      ? alpha(theme.palette.common.white, 0.12)
      : alpha(theme.palette.grey[400], 0.6);

    if (dashboardMode) {
      return {
        borderRadius: 'inherit',
        border: 'none',
        background: 'transparent',
        boxShadow: 'none',
      };
    }

    if (isMapPage) {
      return {
        borderRadius: 0,
        border: 'none',
        background: isDarkMode
          ? theme.palette.background.default
          : theme.palette.grey[50],
        boxShadow: 'none',
      };
    }

    return {
      borderRadius: 2,
      border: `1px solid ${borderColor}`,
      background: theme.palette.background.paper,
      boxShadow: theme.shadows[2],
    };
  }, [dashboardMode, isDarkMode, isMapPage, theme]);

  const rootContainerStyles = useMemo(() => ({
    ...containerStyles,
    width: '100%',
    height,
    minHeight: height,
    position: 'relative',
    overflow: 'hidden',
    transition: 'background 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
  }), [containerStyles, height]);

  const [hoverPosition, setHoverPosition] = useState(null);
  const [isHoveringCard, setIsHoveringCard] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [showRouteDetails, setShowRouteDetails] = useState(false);
  const [routeData, setRouteData] = useState(null);
  const [routeWaypoints, setRouteWaypoints] = useState([]);
  const [userClosedPanel, setUserClosedPanel] = useState(false); // Track if user manually closed the panel
  
  // Debug: Monitor route panel state changes
  useEffect(() => {
  }, [showRouteDetails]);
  
  // Refs to hold previous route state to avoid redundant updates causing loops
  const prevRouteDataRef = useRef(null);
  const prevWaypointsRef = useRef([]);
  const isCalculatingRef = useRef(false);

  // Utility shallow compare for route data (ignore ephemeral fields)
  const isSameRouteData = useCallback((a, b) => {
    if (!a || !b) return false;
    const keysToCompare = [
      'mode',
      'distance',
      'duration',
      'polyline',
      'chargingStops',
      'chargingStations'
    ];
    for (const k of keysToCompare) {
      const av = a[k];
      const bv = b[k];
      if (Array.isArray(av) && Array.isArray(bv)) {
        if (av.length !== bv.length) return false;
        for (let i = 0; i < av.length; i++) {
          if (JSON.stringify(av[i]) !== JSON.stringify(bv[i])) return false;
        }
      } else if (JSON.stringify(av) !== JSON.stringify(bv)) {
        return false;
      }
    }
    // Compare waypoints separately
    if ((a.waypoints?.length || 0) !== (b.waypoints?.length || 0)) return false;
    if (a.waypoints && b.waypoints) {
      for (let i = 0; i < a.waypoints.length; i++) {
        const aw = a.waypoints[i];
        const bw = b.waypoints[i];
        if (aw.location !== bw.location) return false;
        if (aw.coordinates && bw.coordinates) {
          if (aw.coordinates.lat !== bw.coordinates.lat || aw.coordinates.lng !== bw.coordinates.lng) return false;
        }
      }
    }
    return true;
  }, []);

  const setRouteStateSafely = useCallback((newRouteData, newWaypoints, forceUpdate = false) => {
    // Prevent overlapping calculations
    if (isCalculatingRef.current) {
      return;
    }
    const prevRoute = prevRouteDataRef.current;
    const prevWaypoints = prevWaypointsRef.current;
    const routeChanged = !isSameRouteData(newRouteData, prevRoute);
    const waypointsChanged = JSON.stringify(newWaypoints) !== JSON.stringify(prevWaypoints);
    
    
    if (!forceUpdate && !routeChanged && !waypointsChanged) {
      return; // Skip redundant state updates
    }
    
    prevRouteDataRef.current = newRouteData;
    prevWaypointsRef.current = newWaypoints;
    setRouteData(newRouteData);
    setRouteWaypoints(newWaypoints);
  }, [isSameRouteData]);
  const [showRouteChargersOnly, setShowRouteChargersOnly] = useState(false);
  const [isAddingCheckpoint, setIsAddingCheckpoint] = useState(false);

  // Auto-open side panel whenever valid routeData arrives but panel isn't open yet
  // BUT respect if user has manually closed the panel
  useEffect(() => {
    try {
      
      // If we're adding a checkpoint and there's route data, force panel to stay open
      if (isAddingCheckpoint && routeData && !showRouteDetails) {
        setShowRouteDetails(true);
        setUserClosedPanel(false);
        return;
      }
      
      if (routeData && !showRouteDetails && !userClosedPanel) {
        setShowRouteDetails(true);
        // If route has charging info, prefer showing only route chargers
        if ((routeData.chargingStations && routeData.chargingStations.length) || (routeData.chargingStops && routeData.chargingStops.length)) {
          setShowRouteChargersOnly(true);
        }
      } else if (routeData && showRouteDetails) {
      } else if (routeData && userClosedPanel && !isAddingCheckpoint) {
      }
    } catch (error) {
    }
  }, [routeData, showRouteDetails, userClosedPanel, isAddingCheckpoint]);

  // Route calculation functions
  const handleCalculateRoute = useCallback(async (routeRequest) => {
    if (isCalculatingRef.current) {
      return;
    }
    isCalculatingRef.current = true;
    
    // IMPORTANT: Only reset user closed flag for NEW route calculations, not checkpoint additions
    if (!routeRequest?.preserveMode) {
      setUserClosedPanel(false); // Reset user closed flag for new route calculation
    } else {
    }
    
    // Check if this is already a complete route data object (from RoutePlannerPanel)
    // ALWAYS accept Enhanced EV Planner results (even with 0 charging stops)
    const isEvModeFromPlanner = routeRequest.mode === 'ev_advanced' && routeRequest.route;
    const isEvModeWithoutChargingStops = routeRequest.mode === 'ev_advanced' && 
                                        (!routeRequest.chargingStops || routeRequest.chargingStops.length === 0) &&
                                        !routeRequest.route; // Only bypass if no route provided
    
    
    if (routeRequest && routeRequest.mode && routeRequest.waypoints && !routeRequest.preserveMode && (!isEvModeWithoutChargingStops || isEvModeFromPlanner)) {
      
      // Use the route data directly (it already contains EV calculations if applicable)
      setRouteStateSafely(routeRequest, routeRequest.waypoints);
      
      // TEMPORARY: Direct state update to bypass safety check
      setRouteData(routeRequest);
      setRouteWaypoints(routeRequest.waypoints);
      
      setShowRouteChargersOnly(true);
      setShowRouteDetails(true);
      setShowRoutePlanner(false);
      
      isCalculatingRef.current = false;
      return;
    }
    
    // Handle route requests that need to preserve current mode (like adding/removing checkpoints)
    if (routeRequest && routeRequest.preserveMode) {
      
      // CRITICAL: Preserve ALL existing route data including polyline, charging stops, etc.
      const updatedRouteData = {
        ...routeData, // Preserve ALL existing route data from state
        waypoints: routeRequest.waypoints, // Update with new waypoints
        preserveMode: false, // Remove flag
        // Ensure we keep all the routing information with priority to current mode
        mode: routeData?.mode || routeRequest.mode || routePlannerMode || 'simple',
        chargingStops: routeRequest.chargingStops || routeData?.chargingStops,
        chargingStations: routeRequest.chargingStations || routeData?.chargingStations,
        polyline: routeRequest.polyline || routeData?.polyline,
        bounds: routeRequest.bounds || routeData?.bounds,
        route: routeRequest.route || routeData?.route,
        distance: routeRequest.distance || routeData?.distance,
        duration: routeRequest.duration || routeData?.duration
      };
      
      
      // Update route data without losing anything
  setRouteStateSafely(updatedRouteData, updatedRouteData.waypoints);
      setShowRouteChargersOnly(true);
      setShowRouteDetails(true);
      
      // Ensure the route planner mode stays consistent
      if (updatedRouteData.mode === 'ev_advanced' && routePlannerMode !== 'ev_advanced') {
        setRoutePlannerMode('ev_advanced');
      } else if (updatedRouteData.mode === 'simple' && routePlannerMode !== 'simple') {
        setRoutePlannerMode('simple');
      }
      
      isCalculatingRef.current = false;
      return;
    }
    
    // BYPASSED CACHE - PROCEEDING WITH FRESH CALCULATION
    
    // Check if we already have calculated route data (from enhanced EV planner)
    if (routeRequest && typeof routeRequest === 'object' && routeRequest.route) {
      
      // IMPORTANT: Reset calculation flag before setting route state
      isCalculatingRef.current = false;
      
      // Use the already calculated route data
      const combinedRoute = { 
        route: routeRequest.route,
        waypoints: routeRequest.waypoints,
        chargingStops: routeRequest.chargingStops || [],
        chargingStations: routeRequest.chargingStations || routeRequest.chargingStops || [],
        distance: routeRequest.distance,
        duration: routeRequest.duration,
        mode: routeRequest.mode,
        totalChargingTime: routeRequest.totalChargingTime || 0,
        finalSoC: routeRequest.finalSoC,
        energyConsumed: routeRequest.energyConsumed
      };
      
      setRouteStateSafely(combinedRoute, routeRequest.waypoints, true); // Force update for enhanced EV planner
      setShowRouteChargersOnly(true);
      setShowRouteDetails(true);
      setShowRoutePlanner(false); // Hide route planner when route details opens
      
      return;
    }
    
    // Handle both direct waypoints array and route data object
    const waypoints = Array.isArray(routeRequest) ? routeRequest : routeRequest?.waypoints;
    
    
    if (!map || !waypoints || !Array.isArray(waypoints) || waypoints.length < 2) {
      return;
    }

    try {
      // Import route calculation utilities
      const { calculateRouteWithChargingStations, geocodeAddress } = await import('./utils/routeCalculator');
      
      // Geocode waypoints to get coordinates
      const waypointsWithCoords = await Promise.all(
        waypoints.map(async (waypoint) => {
          if (waypoint.coordinates) return waypoint;
          
          const coords = await geocodeAddress(waypoint.location);
          return {
            ...waypoint,
            coordinates: { lat: coords.lat, lng: coords.lng },
            address: coords.address || waypoint.address || 'Address not available'
          };
        })
      );

      // Calculate route with charging stations
      const directionsService = new window.google.maps.DirectionsService();
      const routeResult = await calculateRouteWithChargingStations(
        waypointsWithCoords,
        chargers,
        map,
        directionsService,
        5 // 5km max distance from route
      );

      // Update route data
      const combinedRoute = { ...routeResult, waypoints: waypointsWithCoords };
      setRouteStateSafely(combinedRoute, waypointsWithCoords);
      setShowRouteChargersOnly(true);
      setShowRouteDetails(true);
      setShowRoutePlanner(false); // Hide route planner when route details opens
      
    } catch (error) {
    } finally {
      isCalculatingRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, chargers, routePlannerMode, setShowRoutePlanner, setRoutePlannerMode, setRouteStateSafely]);

  const handleAddCheckpoint = useCallback((charger) => {
    try {
      if (!charger) {
        return;
      }

      setIsAddingCheckpoint(true);

      const wasRouteDetailsVisible = showRouteDetails;
      const currentRoute = prevRouteDataRef.current || routeData;
      const currentWaypoints = (routeWaypoints && routeWaypoints.length > 0
        ? routeWaypoints
        : prevWaypointsRef.current) || [];

      const latitude =
        charger.location?.coordinates?.[1] ??
        charger.locationLatitude ??
        charger.lat ??
        charger.latitude;
      const longitude =
        charger.location?.coordinates?.[0] ??
        charger.locationLongitude ??
        charger.lng ??
        charger.longitude;

      if (latitude == null || longitude == null) {
        setIsAddingCheckpoint(false);
        return;
      }

      const newCheckpoint = {
        location:
          charger.chargepoint_name ||
          charger.station_name ||
          charger.name ||
          charger.locationAddress ||
          'Charging Station',
        locationAddress:
          charger.locationAddress ||
          charger.address ||
          charger.addressLine1 ||
          charger.location?.address ||
          '',
        coordinates: { lat: latitude, lng: longitude },
        chargerId: charger.chargepoint_id || charger.id || charger._id,
        chargerDetails: charger,
        isCharger: true
      };

      if (currentRoute && currentWaypoints && currentWaypoints.length >= 2) {
        const updatedWaypoints = [
          currentWaypoints[0],
          ...currentWaypoints.slice(1, -1),
          newCheckpoint,
          currentWaypoints[currentWaypoints.length - 1]
        ];

        setRouteWaypoints(updatedWaypoints);

        const preservedRouteData = {
          ...currentRoute,
          waypoints: updatedWaypoints,
          chargingStops: currentRoute.chargingStops || [],
          chargingStations: currentRoute.chargingStations || []
        };

        setRouteStateSafely(preservedRouteData, updatedWaypoints, true);

        if (wasRouteDetailsVisible) {
          setShowRouteDetails(true);
          setUserClosedPanel(false);
        }

        setIsAddingCheckpoint(false);
      } else if (currentWaypoints && currentWaypoints.length > 0) {
        const updatedWaypoints = [...currentWaypoints, newCheckpoint];
        setRouteWaypoints(updatedWaypoints);

        if (updatedWaypoints.length >= 2) {
          handleCalculateRoute({
            waypoints: updatedWaypoints,
            mode: routePlannerMode || 'simple'
          });

          setTimeout(() => {
            if (wasRouteDetailsVisible) {
              setShowRouteDetails(true);
              setUserClosedPanel(false);
            }
            setIsAddingCheckpoint(false);
          }, 100);
        }
      } else {
        const newWaypoints = [newCheckpoint];
        setRouteWaypoints(newWaypoints);
      }

      handleChargerHover(null);

      setTimeout(() => {
        setIsAddingCheckpoint(false);
      }, 100);
    } catch (error) {
      setIsAddingCheckpoint(false);
    }
  }, [
    handleCalculateRoute,
    setRouteWaypoints,
    routePlannerMode,
    handleChargerHover,
    showRouteDetails,
    setShowRouteDetails,
    setIsAddingCheckpoint,
    setUserClosedPanel,
    setRouteStateSafely,
    routeWaypoints,
    routeData
  ]);

  const handleRemoveCheckpoint = useCallback((index) => {
    
    const currentRoute = prevRouteDataRef.current;
    const currentWaypoints = prevWaypointsRef.current;
    
    
    if (!currentRoute || !currentWaypoints) {
      return;
    }
    
    if (index <= 0 || index >= currentWaypoints.length - 1) {
      return;
    }


    const updatedWaypoints = currentWaypoints.filter((_, i) => i !== index);
    
    
    // CRITICAL: Preserve the current route mode when removing checkpoint
    const routeRequest = {
      waypoints: updatedWaypoints,
      mode: routePlannerMode || currentRoute.mode || 'simple', // Preserve the current route mode
      preserveMode: true, // Don't recalculate from scratch, just update waypoints
      // Preserve ALL existing route data with fallbacks
      chargingStops: currentRoute.chargingStops || [],
      chargingStations: currentRoute.chargingStations || [],
      polyline: currentRoute.polyline,
      bounds: currentRoute.bounds,
      route: currentRoute.route,
      distance: currentRoute.distance,
      duration: currentRoute.duration
    };
    
    
    // Immediate UI update - update waypoints first for instant feedback
    setRouteWaypoints(updatedWaypoints);
    
    // Then recalculate route
    handleCalculateRoute(routeRequest);
    
  }, [handleCalculateRoute, setRouteWaypoints, routePlannerMode]);

  const handleStartNavigation = useCallback(() => {
    if (routeData && routeWaypoints.length >= 2) {
      // Open Google Maps Navigation with the route
      const start = routeWaypoints[0];
      const destination = routeWaypoints[routeWaypoints.length - 1];
      
      // Build waypoints string for intermediate stops
      let waypointsParam = '';
      if (routeWaypoints.length > 2) {
        const intermediateWaypoints = routeWaypoints.slice(1, -1)
          .map(wp => `${wp.coordinates.lat},${wp.coordinates.lng}`)
          .join('/');
        waypointsParam = `&waypoints=${intermediateWaypoints}`;
      }
      
      const url = `https://www.google.com/maps/dir/?api=1&origin=${start.coordinates.lat},${start.coordinates.lng}&destination=${destination.coordinates.lat},${destination.coordinates.lng}${waypointsParam}&travelmode=driving`;
      window.open(url, '_blank');
    }
  }, [routeData, routeWaypoints]);

  const handleSaveTrip = useCallback(() => {
    
    if (routeData) {
      // Save trip to localStorage or send to backend
      const trip = {
        id: Date.now(),
        name: `Trip to ${routeWaypoints[routeWaypoints.length - 1].location}`,
        waypoints: routeWaypoints,
        distance: routeData.distance,
        duration: routeData.duration,
        createdAt: new Date().toISOString()
      };
      
      const savedTrips = JSON.parse(localStorage.getItem('savedTrips') || '[]');
      savedTrips.push(trip);
      localStorage.setItem('savedTrips', JSON.stringify(savedTrips));
      
    } else {
    }
  }, [routeData, routeWaypoints]);

  const handleShareTrip = useCallback(() => {
    if (routeData) {
      const tripUrl = `${window.location.origin}/map?route=${encodeURIComponent(JSON.stringify(routeWaypoints))}`;
      navigator.clipboard.writeText(tripUrl);
    }
  }, [routeData, routeWaypoints]);

  // Clear route function
  const handleClearRoute = useCallback(() => {
    setRouteData(null);
    setRouteWaypoints([]);
    setShowRouteDetails(false);
    setShowRouteChargersOnly(false);
    setUserClosedPanel(false); // Reset the user closed flag when clearing route
  }, []);

  // Update chargers in context
  useEffect(() => {
    // Guard: avoid resetting chargers if structurally identical
    const areChargersEqual = (a, b) => {
      if (a === b) return true;
      if (!Array.isArray(a) || !Array.isArray(b)) return false;
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        const ac = a[i];
        const bc = b[i];
        // Compare stable identity fields
        const idA = ac.chargepoint_id || ac.id || ac._id || ac.name;
        const idB = bc.chargepoint_id || bc.id || bc._id || bc.name;
        if (idA !== idB) return false;
        // Optional: compare coordinates if present
        const latA = ac.locationLatitude || ac.location?.coordinates?.[1] || ac.lat;
        const latB = bc.locationLatitude || bc.location?.coordinates?.[1] || bc.lat;
        const lngA = ac.locationLongitude || ac.location?.coordinates?.[0] || ac.lng;
        const lngB = bc.locationLongitude || bc.location?.coordinates?.[0] || bc.lng;
        if (latA !== latB || lngA !== lngB) return false;
      }
      return true;
    };

    if (chargers && chargers.length > 0) {
      // Only set if changed from current context list
      if (!areChargersEqual(chargers, filteredChargers)) {
        setChargers(chargers);
        setFilteredChargers(chargers);
      } else {
      }
      
      // Extract filter options from chargers
      const socketTypesMap = {};
      const speedsMap = {};
      const networksMap = {};

      chargers.forEach(charger => {
        // Socket types
        const connectorType = charger.connectorType || charger.type || 'unknown';
        socketTypesMap[connectorType] = (socketTypesMap[connectorType] || 0) + 1;

        // Networks
        const network = charger.network || charger.operator || charger.provider || 'generic';
        networksMap[network] = (networksMap[network] || 0) + 1;

        // Speed (based on power)
        const power = charger.power || charger.kilowatts || 0;
        const speedCategory = power >= 100 ? 'fast' : power >= 50 ? 'medium' : 'slow';
        speedsMap[speedCategory] = (speedsMap[speedCategory] || 0) + 1;
      });

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chargers, filteredChargers]); // filteredChargers included for equality guard

  // Filtering centralized in MapContext; local effect removed to avoid duplication.

  const handleLocationSelect = useCallback((location) => {
    if (map) {
      map.panTo({ lat: location.lat, lng: location.lng });
      map.setZoom(14);
      setMapCenter({ lat: location.lat, lng: location.lng });
      setMapZoom(14);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]); // Intentionally excluding setMapCenter and setMapZoom to avoid infinite recreation

  const toggleFavorite = useCallback((charger) => {
    setFavorites(prev => {
      const isFavorite = prev.some(fav => fav.id === charger.id || fav._id === charger._id);
      if (isFavorite) {
        return prev.filter(fav => fav.id !== charger.id && fav._id !== charger._id);
      } else {
        return [...prev, charger];
      }
    });
  }, []);

  const handleViewOnGoogleMaps = useCallback((charger) => {
    if (charger) {
      const lat = charger.location?.coordinates?.[1] || charger.locationLatitude || charger.lat;
      const lng = charger.location?.coordinates?.[0] || charger.locationLongitude || charger.lng;
      
      if (lat && lng) {
        const googleMapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
        window.open(googleMapsUrl, '_blank');
      }
    }
  }, []);

  const handleToggleFavorite = useCallback((charger, isFavorite) => {
    if (charger) {
      toggleFavorite(charger);
    }
  }, [toggleFavorite]);

  const handleChargerClick = useCallback((charger, chargerGroup = null) => {
    handleChargerSelect(charger, chargerGroup);
  }, [handleChargerSelect]);

  const handleShare = useCallback((charger) => {
    if (charger) {
      const lat = charger.location?.coordinates?.[1] || charger.locationLatitude || charger.lat;
      const lng = charger.location?.coordinates?.[0] || charger.locationLongitude || charger.lng;
      
      if (lat && lng) {
        const shareUrl = `${window.location.origin}/map?lat=${lat}&lng=${lng}&charger=${charger.chargepoint_id}`;
        if (navigator.share) {
          navigator.share({
            title: charger.chargepoint_name || charger.name || 'Charging Station',
            text: `Check out this charging station: ${charger.chargepoint_name || charger.name}`,
            url: shareUrl
          });
        } else {
          navigator.clipboard.writeText(shareUrl);
          // You could show a toast notification here
        }
      }
    }
  }, []);

  const handlePayNow = useCallback((charger) => {
    // Implement payment logic here
  }, []);

  const handleReport = useCallback((charger) => {
    // Implement report logic here
  }, []);

  const handleChargerMouseOver = useCallback((charger) => {
    
    // Clear any pending timeout immediately
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    
    if (charger && map) {
      const lat = charger.location?.coordinates?.[1] || charger.locationLatitude || charger.lat;
      const lng = charger.location?.coordinates?.[0] || charger.locationLongitude || charger.lng;
      
      
      if (lat && lng) {
        // Simple approach: use map bounds to estimate position
        const mapDiv = map.getDiv();
        const mapRect = mapDiv.getBoundingClientRect();
        const bounds = map.getBounds();
        
        if (bounds) {
          const ne = bounds.getNorthEast();
          const sw = bounds.getSouthWest();
          
          // Calculate relative position within bounds
          const latPercent = (lat - sw.lat()) / (ne.lat() - sw.lat());
          const lngPercent = (lng - sw.lng()) / (ne.lng() - sw.lng());
          
          // Convert to screen coordinates
          const position = {
            x: mapRect.left + (lngPercent * mapRect.width),
            y: mapRect.top + ((1 - latPercent) * mapRect.height) // Invert Y since screen Y increases downward
          };
          
          setHoverPosition(position);
          setIsHoveringCard(false);
        }
      }
      
      handleChargerHover(charger);
    }
  }, [map, handleChargerHover]);

  const handleChargerMouseOut = useCallback(() => {
    // Increased timeout for more stable interaction
    hoverTimeoutRef.current = setTimeout(() => {
      if (!isHoveringCard) {
        setHoverPosition(null);
        handleChargerHover(null);
      }
    }, 500); // Increased from 200ms to 500ms for more stability
  }, [handleChargerHover, isHoveringCard]);

  const isFavorite = useCallback((charger) => {
    if (!charger) return false;
    return favorites.some(fav => fav.id === charger.id || fav._id === charger._id);
  }, [favorites]);

  const hoverTimeoutRef = useRef(null);

  const handleCardMouseEnter = useCallback(() => {
    setIsHoveringCard(true);
    // Clear any pending timeout immediately
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  }, []);

  const handleCardMouseLeave = useCallback(() => {
    setIsHoveringCard(false);
    // Start timeout with longer delay for stability
    hoverTimeoutRef.current = setTimeout(() => {
      setHoverPosition(null);
      handleChargerHover(null);
    }, 300); // Slightly shorter than marker timeout for smooth transition
  }, [handleChargerHover]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const getChargerIdentifier = useCallback((charger) => {
    if (!charger) return null;
    return (
      charger.chargepoint_id ||
      charger.id ||
      charger._id ||
      charger.chargerId ||
      charger.uuid ||
      null
    );
  }, []);

  const isWaypointMatchingCharger = useCallback((waypoint, chargerId) => {
    if (!waypoint || !chargerId) return false;
    if (waypoint.chargerId === chargerId) return true;
    const waypointChargerId =
      waypoint.chargerDetails?.chargepoint_id ||
      waypoint.chargerDetails?.id ||
      waypoint.chargerDetails?._id;
    return waypointChargerId === chargerId;
  }, []);

  const isChargerInRoute = useCallback(
    (charger) => {
      const chargerId = getChargerIdentifier(charger);
      if (!chargerId || !routeWaypoints || routeWaypoints.length === 0) {
        return false;
      }
      return routeWaypoints.some((waypoint, index) => {
        if (index === 0 || index === routeWaypoints.length - 1) {
          // Skip origin and destination when checking for checkpoints
          return false;
        }
        return isWaypointMatchingCharger(waypoint, chargerId);
      });
    },
    [getChargerIdentifier, isWaypointMatchingCharger, routeWaypoints]
  );

  const handleRemoveCheckpointByCharger = useCallback(
    (charger) => {
      if (!charger || !routeWaypoints || routeWaypoints.length < 3) {
        return;
      }
      const chargerId = getChargerIdentifier(charger);
      if (!chargerId) {
        return;
      }
      const waypointIndex = routeWaypoints.findIndex((waypoint, index) => {
        if (index === 0 || index === routeWaypoints.length - 1) {
          return false;
        }
        return isWaypointMatchingCharger(waypoint, chargerId);
      });

      if (waypointIndex > 0 && waypointIndex < routeWaypoints.length - 1) {
        handleRemoveCheckpoint(waypointIndex);
      }
    },
    [getChargerIdentifier, handleRemoveCheckpoint, isWaypointMatchingCharger, routeWaypoints]
  );

  const handleRouteDetailsClose = useCallback(() => {
    setShowRouteDetails(false);
    setUserClosedPanel(true);
  }, [setShowRouteDetails, setUserClosedPanel]);

  const activeFilterCount = Object.values(activeFilters).flat().length;

  return (
    <Box sx={rootContainerStyles} className="map-container">
      {/* Search Bar with Filter and Route Planner (Desktop) */}
      {searchBarEnabled && !dashboardMode && (
        <Box
          sx={{
            position: 'absolute',
            top: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            width: '90%',
            maxWidth: 900,
            display: 'flex',
            gap: 1,
            alignItems: 'center',
          }}
        >
          {/* Search Bar */}
          <Box sx={{ flex: 1 }}>
            <SearchBar onLocationSelect={handleLocationSelect} />
          </Box>

          {/* Filter and Route Planner Buttons (Desktop Only) */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              gap: 1,
            }}
          >
            <Box
              component="button"
              onClick={() => setShowRoutePlanner(!showRoutePlanner)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1.5,
                backgroundColor: 'white',
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 25,
                cursor: 'pointer',
                transition: 'all 0.2s',
                color: 'black',
                '&:hover': {
                  backgroundColor: 'white',
                  color: 'grey.600',
                  borderColor: 'grey.400',
                  transform: 'translateY(-1px)',
                  boxShadow: 2,
                },
              }}
            >
              <RouteIcon sx={{ fontSize: 20, color: 'inherit' }} />
              <Box component="span" sx={{ fontWeight: 500, fontSize: 14, color: 'inherit' }}>Plan Route</Box>
            </Box>

            <Box
              component="button"
              onClick={() => setShowFilterPanel(!showFilterPanel)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1.5,
                backgroundColor: 'white',
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 25,
                cursor: 'pointer',
                position: 'relative',
                transition: 'all 0.2s',
                color: 'black',
                '&:hover': {
                  backgroundColor: 'white',
                  color: 'grey.600',
                  borderColor: 'grey.400',
                  transform: 'translateY(-1px)',
                  boxShadow: 2,
                },
              }}
            >
              <FilterIcon sx={{ fontSize: 20, color: 'inherit' }} />
              <Box component="span" sx={{ fontWeight: 500, fontSize: 14, color: 'inherit' }}>Filters</Box>
              {activeFilterCount > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    backgroundColor: 'primary.main',
                    color: 'white',
                    borderRadius: '50%',
                    width: 24,
                    height: 24,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 'bold',
                  }}
                >
                  {activeFilterCount}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}

      {/* Map Container */}
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%' }}>
        <MapContainer
          chargers={showRouteChargersOnly && routeData ? routeData.chargingStations : filteredChargers}
          onChargerClick={handleChargerClick}
          onChargerHover={handleChargerMouseOver}
          onChargerMouseOut={handleChargerMouseOut}
          routeData={routeData}
          showRouteChargersOnly={showRouteChargersOnly}
          onAddCheckpoint={handleAddCheckpoint}
          isRouting={!!routeData}
          onAddToTrip={handleAddCheckpoint}
          onRemoveFromTrip={handleRemoveCheckpoint}
        />
      </Box>

      {/* Map Controls - Show for all except when hiding is specifically needed */}
      {!dashboardMode && (
        <MapControls
          onShowFilters={() => setShowFilterPanel(!showFilterPanel)}
          onShowRoutePlanner={() => setShowRoutePlanner(!showRoutePlanner)}
          activeFilterCount={activeFilterCount}
          hideMobileButtons={false} // Show mobile buttons
          hideDesktopButtons={isMapPage} // Hide desktop Filter and Route only in map page (since they're in top bar)
        />
      )}
      
      {/* Map Controls for Dashboard Mode - minimal version */}
      {dashboardMode && (
        <MapControls
          onShowFilters={() => setShowFilterPanel(!showFilterPanel)}
          onShowRoutePlanner={() => setShowRoutePlanner(!showRoutePlanner)}
          activeFilterCount={activeFilterCount}
          hideMobileButtons={true} // Hide mobile buttons in dashboard
          hideDesktopButtons={true} // Hide desktop Filter and Route in dashboard
        />
      )}


      {/* Filter Panel */}
      {showFilterPanel && (
        <FilterPanel
          onClose={() => setShowFilterPanel(false)}
          filters={activeFilters}
          setFilters={setActiveFilters}
          chargers={chargers}
        />
      )}

      {/* Charger Hover Card */}
      {hoverPosition && hoveredCharger && (
        <ChargerHoverCard
          charger={hoveredCharger}
          position={hoverPosition}
          onViewOnMap={handleViewOnGoogleMaps}
          onToggleFavorite={toggleFavorite}
          isFavorite={isFavorite(hoveredCharger)}
          onAddToTrip={handleAddCheckpoint}
          onRemoveFromTrip={handleRemoveCheckpointByCharger}
          isInRoute={isChargerInRoute(hoveredCharger)}
          isRouting={!!routeData}
        />
      )}

      {/* Detailed Charger Panel */}
      <ChargerInfoPanel
        charger={selectedCharger}
        chargerGroup={chargerGroup}
        isOpen={showChargerInfo}
        onClose={handleCloseChargerInfo}
        onToggleFavorite={toggleFavorite}
        onShare={handleShare}
        onPayNow={handlePayNow}
        onReport={handleReport}
        isFavorite={isFavorite(selectedCharger)}
      />

      {/* Route Planner Drawer */}
      {showRoutePlanner && (
        <RoutePlannerPanel
          onClose={() => setShowRoutePlanner(false)}
          onCalculateRoute={handleCalculateRoute}
        />
      )}

      {/* Route Details Panel */}
      {routeData && (
        <RouteDetailsPanel
          isOpen={showRouteDetails}
          routeData={routeData}
          waypoints={routeWaypoints || []}
          routePlannerMode={routePlannerMode}
          onClose={handleRouteDetailsClose}
          onStartNavigation={handleStartNavigation}
          onSaveTrip={handleSaveTrip}
          onShareTrip={handleShareTrip}
          onAddCheckpoint={handleAddCheckpoint}
          onRemoveFromTrip={handleRemoveCheckpoint}
        />
      )}
    </Box>
  );
};

const AdvancedMapView = ({ chargers = [], searchBarEnabled = true, isMapPage = false, dashboardMode = false, height = '100%' }) => {
  return (
    <MapProvider>
      <MapContent chargers={chargers} searchBarEnabled={searchBarEnabled} isMapPage={isMapPage} dashboardMode={dashboardMode} height={height} />
    </MapProvider>
  );
};

export default AdvancedMapView;
